import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { HiOutlineMenuAlt3, HiOutlineX } from "react-icons/hi";
import { useSelector } from "react-redux";
import NavDropDown from "../DropDown/NavDropDown";
import UserAvatar from "../UserAvatar/UserAvatar";
import { ROUTES } from "../../constants";

const Navbar = ({ user }) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const { isAuthenticated } = useSelector((state) => state.user);
  const { pathname } = useLocation();
  const isAdmin = useSelector((state) => state.user?.user?.role) === "admin";

  useEffect(() => {
    document.body.style.overflow = showNavbar ? "hidden" : "unset";
  }, [showNavbar]);

  // Premium SaaS Link Styling
  const getLinkStyle = (path) => {
    const isActive = pathname === path;
    return `relative px-4 py-2 text-sm font-bold transition-all duration-300 rounded-full flex items-center justify-center
      ${isActive 
        ? "text-sky-600 bg-sky-50/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] border border-sky-100/50" 
        : "text-slate-500 hover:text-slate-900 hover:bg-slate-100/50 border border-transparent"
      }`;
  };

  return (
    <>
      {/* Main Nav Container - Frosted Glass Effect */}
      <nav className="bg-white/80 sticky top-0 z-[100] border-b border-slate-200/60 backdrop-blur-xl transition-all">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* 1. Logo Section */}
          <Link to={ROUTES.home} className="flex items-center gap-3 group">
            <div className="relative">
              <img 
                src={logo} 
                alt="CampusIRA" 
                className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-105" 
              />
            </div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 hidden sm:block">
              Campus<span className="text-sky-600">IRA</span>
            </h1>
          </Link>

          {/* 2. Desktop Navigation Architecture */}
          <div className="hidden lg:flex items-center bg-slate-50/80 p-1.5 rounded-full border border-slate-200/50 backdrop-blur-md shadow-sm">
            <Link to={ROUTES.home} className={getLinkStyle(ROUTES.home)}>Home</Link>
            <Link to={ROUTES.posts} className={getLinkStyle(ROUTES.posts)}>Experiences</Link>
            <Link to={ROUTES.qaPage} className={getLinkStyle(ROUTES.qaPage)}>Q&A</Link>
            <Link to={ROUTES.resources} className={getLinkStyle(ROUTES.resources)}>Library</Link>
            <Link to={ROUTES.jobs} className={getLinkStyle(ROUTES.jobs)}>Job Alerts</Link>
            <Link to={ROUTES.help} className={getLinkStyle(ROUTES.help)}>Feedback</Link>
            
            {/* The Hiring Link with Status Pulse */}
            <Link to="/hiring" className={`${getLinkStyle("/hiring")} gap-2`}>
              Hiring
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
              </span>
            </Link>

            {isAdmin && (
              <Link to={ROUTES.adminFeedback} className={getLinkStyle(ROUTES.adminFeedback)}>
                Admin
              </Link>
            )}
          </div>

          {/* 3. Auth Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownVisible(!isDropdownVisible)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-full border transition-all duration-300 ${
                    isDropdownVisible 
                    ? 'bg-white border-sky-200 shadow-md ring-2 ring-sky-50' 
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <UserAvatar src={user?.profileImage} name={user?.name} size={32} />
                  <span className="text-sm font-bold text-slate-800 pr-2">{user?.name}</span>
                </button>
                {isDropdownVisible && <NavDropDown toggleDropdown={() => setDropdownVisible(false)} />}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Link
                  to={ROUTES.login}
                  className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to={ROUTES.login}
                  className="bg-slate-900 text-white px-6 py-2.5 rounded-full font-bold text-sm shadow-md hover:bg-slate-800 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2.5 text-slate-900 bg-slate-100/50 border border-slate-200 rounded-full hover:bg-slate-100 transition-all" 
              onClick={() => setShowNavbar(true)}
            >
              <HiOutlineMenuAlt3 size={20} />
            </button>
          </div>
        </div>
      </nav>

      {/* 4. Full Screen Premium Mobile Menu */}
      <div 
        className={`fixed inset-0 z-[1000] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          showNavbar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-[#FAFAFA]/95 backdrop-blur-3xl" />

        <div className="relative h-full flex flex-col p-8">
          <div className="flex justify-between items-center mb-12">
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Campus<span className="text-sky-600">IRA</span></h1>
            <button 
              onClick={() => setShowNavbar(false)} 
              className="p-3 bg-white border border-slate-200 text-slate-900 rounded-full hover:rotate-90 shadow-sm transition-all duration-300"
            >
              <HiOutlineX size={20} />
            </button>
          </div>

          <div className="flex flex-col gap-6 overflow-y-auto pb-8">
            {[
              { label: 'Home', route: ROUTES.home },
              { label: 'Experiences', route: ROUTES.posts },
              { label: 'Q&A', route: ROUTES.qaPage },
              { label: 'Library', route: ROUTES.resources },
              { label: 'Job Alerts', route: ROUTES.jobs },
              { label: 'Feedback', route: ROUTES.help },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.route}
                onClick={() => setShowNavbar(false)}
                className="text-3xl font-extrabold text-slate-800 hover:text-sky-600 hover:translate-x-2 transition-all duration-300"
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Hiring Link */}
            <Link
              to="/hiring"
              onClick={() => setShowNavbar(false)}
              className="text-3xl font-extrabold text-sky-600 flex items-center gap-3 hover:translate-x-2 transition-all duration-300"
            >
              We're Hiring
              <span className="px-3 py-1 bg-sky-100 text-sky-700 text-xs font-black uppercase tracking-widest rounded-full align-middle">
                Live
              </span>
            </Link>
          </div>

          <div className="mt-auto pt-8 border-t border-slate-200">
            {!isAuthenticated ? (
              <div className="grid grid-cols-2 gap-4">
                <Link to={ROUTES.login} onClick={() => setShowNavbar(false)} className="py-4 text-center font-bold text-slate-700 bg-white border border-slate-200 rounded-2xl shadow-sm">Sign In</Link>
                <Link to={ROUTES.login} onClick={() => setShowNavbar(false)} className="py-4 text-center font-bold text-white bg-slate-900 rounded-2xl shadow-xl">Get Started</Link>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                 <UserAvatar src={user?.profileImage} name={user?.name} size={40} />
                 <div>
                   <p className="text-sm text-slate-500 font-medium">Signed in as</p>
                   <p className="font-bold text-slate-900">{user?.name}</p>
                 </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;